<template>
  <nav class="breadcrumbs">
    <img
      class="icon"
      src="@/assets/images/forward-rounded-orange.svg"
      width="7"
      height="13"
      alt=""
    />
    <a
      v-if="referrerRoute"
      :href="`${referrerRoute.fullPath}`"
      @click.stop.prevent="backToResults"
      ><translate>Go back to your search results</translate>
    </a>
    <pu-search-link
      v-else-if="isLinkable"
      :modify-filters="{ sort: undefined }"
      :hierarchy-path="hierarchyPath"
      category="campsites"
    >
      <translate :tt="{ hierarchyText }">
        See listings in {hierarchyText}
      </translate>
    </pu-search-link>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import { routeToFilters } from '~/apps/pu-links/infrastructure/routeToFilters'
import type { BreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import { useBreadcrumbCounts } from '~/composables/useBreadcrumbCounts'

interface Props {
  hierarchyPath: string
  hierarchyText?: string
  referrerRoute?: RouteLocationNormalized
  ptsViewed?: number
}

const props = withDefaults(defineProps<Props>(), {
  hierarchyText: '',
  referrerRoute: undefined,
  ptsViewed: 0,
})

const router = useRouter()
const route = useRoute()

const searchFilters = computed(() => ({
  ...routeToFilters(route),
  hierarchyPath: props.hierarchyPath,
  facets: [],
}))

const { breadcrumbCounts, hasFacets } = useBreadcrumbCounts(searchFilters)

const hierarchyCounts = computed<Record<string, number>>(() => {
  const counts = breadcrumbCounts.value as BreadcrumbCounts | undefined
  return counts?.bookableFacetHierarchy ?? {}
})

const isLinkable = computed(() => {
  const hierarchyCount = props.hierarchyPath
    ? hierarchyCounts.value[props.hierarchyPath]
    : undefined
  const countLevel = hasFacets.value ? 5 : 1
  return hierarchyCount && hierarchyCount >= countLevel
})

const backToResults = () => {
  const stepsToGoBack = props.ptsViewed * 2 + 1
  router.go(-stepsToGoBack)
}
</script>

<style>
@import url('~/assets/css/breadcrumbs.css');
</style>
